import { useState, useEffect, useCallback } from 'react'
import { api } from '@/config/api'
import type {
  UserSummary,
  UserInfo,
  GreenmateInfo,
  UserStats,
  UserDetailResponse,
  Pagination,
  UsersResponse,
  Filters,
} from '@/types'

export function useUsers(filters: Filters) {
  const [users, setUsers] = useState<UserSummary[]>([])
  const [pagination, setPagination] = useState<Pagination | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    const fetchUsers = async () => {
      setLoading(true)
      setError(null)
      try {
        const params: Record<string, unknown> = {
          page: filters.page,
          limit: filters.limit,
          sort_by: filters.sortBy,
          order: filters.order,
        }
        if (filters.search) params.search = filters.search
        if (filters.dateFrom) params.date_from = filters.dateFrom
        if (filters.dateTo) params.date_to = filters.dateTo

        const { data } = await api.get<UsersResponse>('/api/users', { params })
        setUsers(data.users)
        setPagination(data.pagination)
      } catch (err) {
        setError((err as Error).message)
        console.error('Failed to fetch users:', err)
      } finally {
        setLoading(false)
      }
    }
    fetchUsers()
  }, [filters.page, filters.limit, filters.sortBy, filters.order, filters.search, filters.dateFrom, filters.dateTo])

  return { users, pagination, loading, error }
}

export function useUserDetail(userId: string) {
  const [user, setUser] = useState<UserInfo | null>(null)
  const [greenmate, setGreenmate] = useState<GreenmateInfo | null>(null)
  const [stats, setStats] = useState<UserStats | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  const refetch = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const { data } = await api.get<UserDetailResponse>(`/api/users/${userId}`)
      setUser(data.user)
      setGreenmate(data.greenmate)
      setStats(data.stats)
    } catch (err) {
      setError((err as Error).message)
      console.error('Failed to fetch user:', err)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    refetch()
  }, [refetch])

  return { user, greenmate, stats, loading, error, refetch }
}
